import Add from '@mui/icons-material/Add'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'preact/hooks'
import { api } from '../api/client'
import type { EventSummary } from '../api/generated/EventSummary'
import type { Me } from '../api/generated/Me'
import { useApi } from '../api/useApi'
import { PasskeyOffer } from '../auth/PasskeyOffer'
import { useSession } from '../auth/session'
import { EventCard } from '../events/EventCard'
import { SectionPage } from '../events/SectionPage'
import { splitEvents } from '../events/summaries'

function NameForm() {
  const { setMe } = useSession()
  const [name, setName] = useState('')
  const saveName = async (event: Event) => {
    event.preventDefault()
    setMe(await api.patch<Me>('/me', { display_name: name }))
  }
  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.5 }, mb: 3 }}>
      <Stack component="form" spacing={2} onSubmit={saveName}>
        <Typography variant="h6" component="h2">
          Bienvenue ! Comment vous appelez-vous ?
        </Typography>
        <TextField
          label="Prénom et nom"
          autoComplete="name"
          required
          autoFocus
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
        />
        <Button type="submit" variant="contained" sx={{ alignSelf: 'flex-start' }}>
          Continuer
        </Button>
      </Stack>
    </Paper>
  )
}

const grid = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
  gap: 2,
} as const

function EmptyState({ canCreate }: { canCreate: boolean }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 }, textAlign: 'center' }}>
      <Typography variant="h6" component="h2" gutterBottom>
        Aucun événement pour l'instant
      </Typography>
      {canCreate ? (
        <>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Créez votre événement, ses postes et ses créneaux, puis invitez les bénévoles.
          </Typography>
          <Button variant="contained" startIcon={<Add />} href="/events/new">
            Créer un événement
          </Button>
        </>
      ) : (
        <Typography color="text.secondary">
          Vous serez invité par un organisateur : ouvrez le lien reçu par email ou SMS, ou
          inscrivez-vous depuis la page d'inscription de l'événement.
        </Typography>
      )}
    </Paper>
  )
}

function PastEvents({ events, now }: { events: EventSummary[]; now: Date }) {
  const [open, setOpen] = useState(false)
  return (
    <Box sx={{ mt: 3 }}>
      <Button
        color="inherit"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        endIcon={
          <ExpandMore sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
        }
      >
        Événements passés ({events.length})
      </Button>
      <Collapse in={open}>
        <Box sx={{ ...grid, mt: 1.5 }}>
          {events.map((event) => (
            <EventCard key={event.slug} event={event} now={now} />
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}

function MyEvents({ canCreate }: { canCreate: boolean }) {
  const { data: events, error } = useApi<EventSummary[]>('/events')
  if (error) return <Alert severity="error">{error}</Alert>
  if (!events) return <CircularProgress />
  if (events.length === 0) return <EmptyState canCreate={canCreate} />
  const now = new Date()
  const { upcoming, past } = splitEvents(events, now)
  return (
    <>
      <Box sx={grid}>
        {upcoming.map((event) => (
          <EventCard key={event.slug} event={event} now={now} />
        ))}
      </Box>
      {past.length > 0 && <PastEvents events={past} now={now} />}
    </>
  )
}

/** The user's events, after login. */
export function Home() {
  const { me } = useSession()
  if (!me) return null
  return (
    <>
      <SectionPage
        title="Mes événements"
        overline={me.display_name ? `Bonjour ${me.display_name}` : undefined}
        actions={
          me.can_create_events && (
            <Button variant="contained" startIcon={<Add />} href="/events/new">
              Créer un événement
            </Button>
          )
        }
      >
        <PasskeyOffer />
        {!me.display_name && <NameForm />}
        <MyEvents canCreate={me.can_create_events} />
      </SectionPage>
    </>
  )
}
